import Phaser from 'phaser';
import { COLORS } from '../theme';

/**
 * Animated background equalizer bars (22 vertical bars) behind the dancing character.
 * Each bar tweens between min/max heights at staggered phases. "hype" alternates to
 * boost amplitude/glow to mimic the design's hype radial flash.
 *
 * Amplitude is multiplied by a `level` (default 0.45) so the idle state reads as a
 * subdued pulse and a hit can amp it up via setLevel(1.2). Calling setLevel cancels
 * the running per-bar yoyo tweens and restarts them with the new target — the
 * staggered delays mean the new amplitude phases in over ~900ms rather than snapping.
 */
export class EqualizerBg {
  container: Phaser.GameObjects.Container;
  private bars: Phaser.GameObjects.Rectangle[] = [];
  private barTweens: Phaser.Tweens.Tween[] = [];
  private hype = true;
  private baseColors = [COLORS.coral, COLORS.yolk, COLORS.white];
  private fullTargetScaleY = 1;
  private currentLevel: number;

  constructor(
    private scene: Phaser.Scene,
    private x: number,
    private y: number,
    private width: number,
    private height: number,
    private barCount = 22,
    baseLevel = 0.45,
  ) {
    this.currentLevel = baseLevel;
    this.container = scene.add.container(x, y);
    this.container.setDepth(2);
    this.buildBars();
    this.startBarTweens();
    this.startHypeLoop();
  }

  private buildBars() {
    const gap = 6;
    const barW = (this.width - (this.barCount - 1) * gap) / this.barCount;
    const startH = this.height * 0.2;
    const targetH = this.height * 0.55;
    // Tween scaleY (not raw height) so the rectangle grows symmetrically around its
    // origin — Phaser's Rectangle.height setter does NOT update displayOriginY, which
    // makes a height tween appear to grow downward regardless of origin.
    this.fullTargetScaleY = targetH / startH;
    for (let i = 0; i < this.barCount; i++) {
      const color = this.baseColors[i % this.baseColors.length];
      const bx = -this.width / 2 + i * (barW + gap) + barW / 2;
      // Bars anchored near the character's waist (bottom of the zone). With origin
      // (0.5, 1), scaling Y expands UPWARD from the anchor.
      const bar = this.scene.add
        .rectangle(bx, this.height / 2, barW, startH, color, 0.6)
        .setOrigin(0.5, 1);
      this.container.add(bar);
      this.bars.push(bar);
    }
  }

  private effectiveTargetScaleY(level: number) {
    // level=0 → no movement (target = 1, same as start). level=1 → full original
    // range (target = fullTargetScaleY). Linear blend.
    return 1 + level * (this.fullTargetScaleY - 1);
  }

  private startBarTweens() {
    const target = this.effectiveTargetScaleY(this.currentLevel);
    this.bars.forEach((bar, i) => {
      // Stagger tween durations and delays (translated from pop-v2's @keyframes eqBar0-4).
      const dur = 600 + (i % 5) * 80;
      const delay = (i * 70) % 900;
      const tween = this.scene.tweens.add({
        targets: bar,
        scaleY: target,
        duration: dur,
        delay,
        ease: 'Sine.InOut',
        yoyo: true,
        repeat: -1,
      });
      this.barTweens.push(tween);
    });
  }

  /** Set the amplitude multiplier (0 = flat, 1 = original full range). Stops the
   * running per-bar tweens and restarts them with the new target so the change
   * phases in over the existing staggered delays rather than snapping. */
  setLevel(level: number) {
    if (Math.abs(level - this.currentLevel) < 0.01) return;
    this.currentLevel = level;
    this.barTweens.forEach((t) => t.stop());
    this.barTweens = [];
    this.bars.forEach((bar) => bar.setScale(bar.scaleX, 1));
    this.startBarTweens();
  }

  private startHypeLoop() {
    this.scene.time.addEvent({
      delay: 1800,
      loop: true,
      callback: () => {
        this.hype = !this.hype;
        this.bars.forEach((b) => {
          this.scene.tweens.add({
            targets: b,
            alpha: this.hype ? 0.85 : 0.5,
            duration: 300,
          });
        });
      },
    });
  }

  setVisible(v: boolean) {
    this.container.setVisible(v);
  }

  destroy() {
    this.container.destroy();
  }
}
